import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box } from '@mui/material';

// Width moved per ArrowLeft/ArrowRight press while the handle has focus.
const KEY_STEP_PX = 24;

function widthOf(element) {
  return element ? element.getBoundingClientRect().width : 0;
}

// Both neighbors' current widths, as the after pane's width and its bounds.
function measureBounds(handle, minBefore, minAfter) {
  const before = widthOf(handle.previousElementSibling);
  const after = widthOf(handle.nextElementSibling);
  return {
    after,
    minAfter,
    maxAfter: Math.max(minAfter, before + after - minBefore),
  };
}

// The after pane's width once the divider has moved deltaX px to the right.
function clampedAfterWidth({ after, minAfter, maxAfter }, deltaX) {
  return Math.min(maxAfter, Math.max(minAfter, after - deltaX));
}

// The separator's ARIA value: the after pane's width in px, within its bounds.
function ariaValue({ minAfter, maxAfter }, width) {
  return {
    min: Math.round(minAfter),
    max: Math.round(maxAfter),
    now: Math.round(width),
  };
}

/**
 * A draggable vertical divider between two side-by-side panes. It resizes
 * the pane AFTER it (the parent owns that width, via onAfterWidthChange);
 * the pane BEFORE it is flexible and takes the remainder. On each drag or
 * key press it measures both neighbors and keeps their combined width
 * constant, never letting the before pane go below minBefore or the after
 * pane below minAfter (minAfter wins when there is no room for both).
 *
 * Its ARIA value (aria-valuenow/min/max) is the after pane's width in px,
 * measured on mount and on focus and updated on every move.
 *
 * Mouse events, not pointer events: desktop-first, and testable in jsdom.
 */
const ColumnResizeHandle = ({
  label,
  minBefore,
  minAfter,
  onAfterWidthChange,
}) => {
  const handleRef = useRef(null);
  const dragRef = useRef(null);
  const [value, setValue] = useState(null);
  // Moves the divider: tells the parent the after pane's new width.
  const moveRef = useRef(null);
  moveRef.current = (bounds, width) => {
    setValue(ariaValue(bounds, width));
    onAfterWidthChange(width);
  };

  const measure = () => measureBounds(handleRef.current, minBefore, minAfter);
  const refreshValue = useCallback(() => {
    const bounds = measureBounds(handleRef.current, minBefore, minAfter);
    setValue(ariaValue(bounds, bounds.after));
  }, [minBefore, minAfter]);

  useLayoutEffect(refreshValue, [refreshValue]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const drag = dragRef.current;
      if (drag)
        moveRef.current(
          drag.start,
          clampedAfterWidth(drag.start, event.clientX - drag.startX)
        );
    };
    const handleMouseUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      handleMouseUp();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragRef.current = { startX: event.clientX, start: measure() };
    // Keep the resize cursor, and avoid selecting text, while dragging
    // across the panes.
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleKeyDown = (event) => {
    const step = { ArrowLeft: -KEY_STEP_PX, ArrowRight: KEY_STEP_PX }[
      event.key
    ];
    if (step === undefined) return;
    event.preventDefault();
    const bounds = measure();
    moveRef.current(bounds, clampedAfterWidth(bounds, step));
  };

  return (
    <Box
      ref={handleRef}
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={value?.now}
      aria-valuemin={value?.min}
      aria-valuemax={value?.max}
      tabIndex={0}
      onFocus={refreshValue}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      sx={{
        width: 6,
        flexShrink: 0,
        cursor: 'col-resize',
        bgcolor: 'divider',
        '&:hover, &:focus-visible': {
          bgcolor: 'primary.light',
          outline: 'none',
        },
      }}
    />
  );
};

export default ColumnResizeHandle;
